/**
 * auth.js
 * Token + user session storage utilities.
 * All auth state lives here — no credentials ever stored, only JWT tokens.
 */

const ACCESS_TOKEN_KEY  = 'act_access_token';
const REFRESH_TOKEN_KEY = 'act_refresh_token';
const USER_KEY          = 'act_user';

// ─── Token storage ────────────────────────────────────────────────────────────

export const storeAuthData = ({ accessToken, refreshToken, user }) => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY,  accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return true;
  } catch {
    return false;
  }
};

export const clearAuthData = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ─── JWT decode (no verify — server already verified) ────────────────────────

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const decoded = decodeJwt(token);
  if (!decoded?.exp) return true;
  // Add 10s buffer so we refresh slightly before actual expiry
  return decoded.exp * 1000 < Date.now() + 10_000;
}

// ─── Session check ────────────────────────────────────────────────────────────

/**
 * Returns { isValid, user } from localStorage.
 * Used on app load to restore session without an API call.
 */
export const getAuthData = () => {
  const accessToken  = getAccessToken();
  const refreshToken = getRefreshToken();
  const user         = getStoredUser();

  if (!accessToken || !user) return { isValid: false, user: null };

  // If access token is still valid — session is good
  if (!isTokenExpired(accessToken)) {
    return { isValid: true, user };
  }

  // Access token expired — only attempt refresh if refresh token is present AND not expired
  if (refreshToken && !isTokenExpired(refreshToken)) {
    return { isValid: false, user, needsRefresh: true };
  }

  // Both tokens expired (or no refresh token) — clear stale data
  clearAuthData();
  return { isValid: false, user: null };
};

export const isAuthenticated = () => getAuthData().isValid;
export const getCurrentUser  = () => getStoredUser();
